import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  LinearProgress,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material'
import {
  scheduleService,
  ScheduleReport,
  ReportEnrollmentDetail,
} from '../services/scheduleService'

const TestScheduleReportPage: React.FC = () => {
  const { t, i18n } = useTranslation('test')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [report, setReport] = useState<ScheduleReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadReport = async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const data = await scheduleService.getGroupScheduleReport(id)
      setReport(data)
    } catch (err) {
      setError(t('report.errors.loadFailed'))
      console.error('Report loading error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-'
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'
    return new Date(dateString).toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getEnrollmentStatusChip = (status: string) => {
    switch (status) {
      case 'ASSIGNED':
        return <Chip size="small" label={t('schedule.enrollmentStatus.ASSIGNED')} />
      case 'IN_PROGRESS':
        return (
          <Chip size="small" label={t('schedule.enrollmentStatus.IN_PROGRESS')} color="secondary" />
        )
      case 'COMPLETED':
        return (
          <Chip size="small" label={t('schedule.enrollmentStatus.COMPLETED')} color="primary" />
        )
      case 'OVERDUE':
        return (
          <Chip size="small" label={t('schedule.enrollmentStatus.OVERDUE')} color="secondary" />
        )
      default:
        return <Chip size="small" label={status} />
    }
  }

  const getRateColor = (rate: number) => {
    if (rate >= 80) return 'primary.main'
    if (rate >= 50) return 'secondary.main'
    return 'text.secondary'
  }

  const handlePrintPdf = () => {
    window.print()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!report) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('report.notFound')}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/test-schedules')}
          sx={{ mt: 2 }}
        >
          {t('schedule.detail.backToList')}
        </Button>
      </Box>
    )
  }

  const { scheduleInfo, report: reportData, enrollments } = report

  return (
    <Box sx={{ p: 3 }}>
      {/* パンくずリスト */}
      <Breadcrumbs sx={{ mb: 2 }} className="no-print">
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/test-schedules')}
        >
          {t('schedule.detail.breadcrumb')}
        </Link>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate(`/test-schedules/${id}`)}
        >
          {t('schedule.detail.title')}
        </Link>
        <Typography color="text.primary">{t('report.title')}</Typography>
      </Breadcrumbs>

      {/* ヘッダー */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <AssessmentIcon fontSize="large" color="primary" className="no-print" />
            {t('report.pageTitle')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mt: 0.5 }}>
            {scheduleInfo.testTitle}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {scheduleInfo.category && <Chip label={scheduleInfo.category} size="small" />}
            <Chip
              icon={<GroupIcon />}
              label={scheduleInfo.groupName}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} className="no-print">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/test-schedules/${id}`)}>
            {t('report.backToDetail')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PdfIcon />}
            onClick={handlePrintPdf}
          >
            {t('report.exportPdf')}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* スケジュール期間 */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Stack
          className="print-row"
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('report.period.start')}
            </Typography>
            <Typography variant="h6">{formatDate(scheduleInfo.startDate)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon color="action" />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('report.period.end')}
            </Typography>
            <Typography variant="h6">{formatDate(scheduleInfo.endDate)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
            <Typography color="text.secondary">|</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('report.period.dueDate')}
            </Typography>
            <Typography variant="h6" color="primary">
              {formatDate(scheduleInfo.dueDate)}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* 主要指標カード */}
      <Box
        className="print-row"
        sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}
      >
        {/* 受講完了率 */}
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ p: { xs: 2, print: 1 } }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <SchoolIcon color="primary" fontSize="small" className="no-print" />
              <Typography variant="subtitle2" color="text.secondary">
                {t('report.metrics.completionRate')}
              </Typography>
            </Stack>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                color: getRateColor(reportData.completionRate),
                fontSize: { xs: '2rem', print: '1.5rem' },
              }}
            >
              {reportData.completionRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('report.metrics.completedOfTotal', {
                completed: reportData.completedCount,
                total: reportData.totalCount,
              })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={reportData.completionRate}
              sx={{ height: 6, borderRadius: 3, mt: 1 }}
            />
          </CardContent>
        </Card>

        {/* 期限内完了率 */}
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ p: { xs: 2, print: 1 } }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <TimerIcon color="primary" fontSize="small" className="no-print" />
              <Typography variant="subtitle2" color="text.secondary">
                {t('report.metrics.onTimeRate')}
              </Typography>
            </Stack>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                color: getRateColor(reportData.onTimeCompletionRate),
                fontSize: { xs: '2rem', print: '1.5rem' },
              }}
            >
              {reportData.onTimeCompletionRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('report.metrics.onTimeOfCompleted', {
                onTime: reportData.onTimeCompletedCount,
                completed: reportData.completedCount,
              })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={reportData.onTimeCompletionRate}
              color={
                reportData.onTimeCompletionRate >= 80
                  ? 'success'
                  : reportData.onTimeCompletionRate >= 50
                    ? 'warning'
                    : 'error'
              }
              sx={{ height: 6, borderRadius: 3, mt: 1 }}
            />
          </CardContent>
        </Card>

        {/* 平均テストスコア */}
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ p: { xs: 2, print: 1 } }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <TrendingUpIcon color="primary" fontSize="small" className="no-print" />
              <Typography variant="subtitle2" color="text.secondary">
                {t('report.metrics.averageScore')}
              </Typography>
            </Stack>
            <Typography
              variant="h3"
              fontWeight="bold"
              color="primary"
              sx={{ fontSize: { xs: '2rem', print: '1.5rem' } }}
            >
              {reportData.averageScore}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('report.metrics.passingScore', { score: scheduleInfo.passingScore })}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={t('report.metrics.passedCount', { count: reportData.passedCount })}
                color="primary"
                size="small"
              />
              <Chip
                label={t('report.metrics.passRate', { rate: reportData.passRate })}
                size="small"
                variant="outlined"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* 未受講者数 */}
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ p: { xs: 2, print: 1 } }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <PersonIcon color="primary" fontSize="small" className="no-print" />
              <Typography variant="subtitle2" color="text.secondary">
                {t('report.metrics.notStarted')}
              </Typography>
            </Stack>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                color: reportData.notStartedCount > 0 ? 'secondary.main' : 'primary.main',
                fontSize: { xs: '2rem', print: '1.5rem' },
              }}
            >
              {reportData.notStartedCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('report.metrics.ofTotal', { total: reportData.totalCount })}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
              <Chip
                label={t('report.metrics.inProgress', { count: reportData.inProgressCount })}
                size="small"
                color="secondary"
              />
              <Chip
                label={t('report.metrics.overdue', { count: reportData.overdueCount })}
                size="small"
                color="secondary"
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* 受講者一覧 */}
      <Paper sx={{ p: 1.5 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
        >
          <PersonIcon color="primary" className="no-print" />
          {t('report.enrolleeList')}
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>{t('schedule.enrollee.name')}</TableCell>
                <TableCell>{t('schedule.enrollee.email')}</TableCell>
                <TableCell>{t('schedule.enrollee.department')}</TableCell>
                <TableCell>{t('schedule.enrollee.status')}</TableCell>
                <TableCell>{t('report.table.score')}</TableCell>
                <TableCell>{t('report.table.result')}</TableCell>
                <TableCell>{t('report.table.completedAt')}</TableCell>
                <TableCell>{t('report.table.onTime')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrollments.map((enrollment: ReportEnrollmentDetail) => (
                <TableRow key={enrollment.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {enrollment.user.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{enrollment.user.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{enrollment.user.department || '-'}</Typography>
                  </TableCell>
                  <TableCell>{getEnrollmentStatusChip(enrollment.status)}</TableCell>
                  <TableCell>
                    {enrollment.latestResult ? (
                      <Typography variant="body2" fontWeight="bold">
                        {enrollment.latestResult.score}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {enrollment.latestResult ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {enrollment.latestResult.passed ? (
                          <>
                            <CheckCircleIcon color="primary" fontSize="small" />
                            <Chip size="small" label={t('result.passed')} color="primary" />
                          </>
                        ) : (
                          <>
                            <CancelIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                            <Chip size="small" label={t('result.failed')} color="default" />
                          </>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {enrollment.latestResult
                        ? formatDateTime(enrollment.latestResult.completedAt)
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {enrollment.latestResult ? (
                      enrollment.isOnTime ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={t('report.onTime.yes')}
                          color="primary"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label={t('report.onTime.no')}
                          color="secondary"
                          size="small"
                        />
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  )
}

export default TestScheduleReportPage
